'use client';

import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

import Load from '../../components/Load';
import {
    TORNEIOS_FILTRADOS,
    TorneiosFiltradosData,
    TorneiosFiltradosVars,
    Torneio,
} from '../../services/torneioService';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { getMonthAbbreviation } from '../../utils/date';
import { useDebounce } from '../../hooks/useDebounce';

import Header from '../../components/Header';
import Leftnav from '../../components/Leftnav';
import Rightchat from '../../components/Rightchat';
import Appfooter from '../../components/Appfooter';
import Popupchat from '../../components/Popupchat';
import TournamentFilterModal, { TournamentFilters } from '../../components/TournamentFilterModal';

import { StatusTorneio } from '../../utils/StatusTorneio';


const PAGE_SIZE = 10;

const Event: React.FC = () => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [page, setPage] = useState(0);
    const [filterOpen, setFilterOpen] = useState(false);
    const filterBtnRef = useRef<HTMLButtonElement>(null);
    const [filters, setFilters] = useState<TournamentFilters>({});
    const auth = useAuth();
    const { currentProfile } = useProfile();

    const [fetchFiltered, { data: filteredData, loading: loadingFiltered, error }] = useLazyQuery<
        TorneiosFiltradosData,
        TorneiosFiltradosVars
    >(TORNEIOS_FILTRADOS, { fetchPolicy: 'no-cache' });

    useEffect(() => {
        const isAdmin = (auth?.user as { role?: string } | null)?.role === 'ADMIN';
        const isLigaProfile = currentProfile?.tipo === 'LIGA';
        const ligaId = !isAdmin && isLigaProfile ? currentProfile.referenciaId : null;
        
        fetchFiltered({
            variables: {
                nome: debouncedSearch || null,
                cidade: filters.cidade?.nome || null,
                data: filters.data || null,
                status: filters.status ? (filters.status as StatusTorneio) : null,
                ativo: true,
                ocultarPassados: !filters.verEncerrados,
                ligaId,
                direction: 'ASC',
                page,
                size: PAGE_SIZE,
                sortBy: 'dataInicio',
            },
        });
    }, [debouncedSearch, page, filters, currentProfile, auth?.user, fetchFiltered]);

    const torneios = filteredData?.torneiosFiltrados.items || [];

    // dataInicio é opcional na API (criarTorneio aceita só nome); sem data,
    // o torneio não é exibível nesta lista (ordenada por dataInicio) — mesma
    // regra aplicada no widget Events.
    const eventsList = torneios.filter((torneio) => !!torneio.dataInicio).map((torneio) => {
        const [year, month, day] = torneio.dataInicio.split('-').map(Number)
        const localDate = new Date(year, month - 1, day)
        return {
            id: torneio.id,
            date: localDate.getDate().toString(),
            month: getMonthAbbreviation(localDate),
            status:
                localDate.getTime() < Date.now()
                    ? 'bg-success'
                    : 'bg-warning',
            name: torneio.nome,
            location: torneio.local?.nome,
            imageUrl: torneio.bannerUrl || '../assets/images/comingsoon.svg',
            categories: torneio.categoriasTorneio?.map(c => c.nome).join(', ') || '',
        }
    })

    return (
        <Fragment>
            <Header />

            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left pe-0">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="d-flex align-items-center justify-content-between bg-white p-4 rounded-3 mb-4">
                                    <h2 className="fw-700 mb-0">Torneios</h2>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="position-relative" style={{ maxWidth: 250 }}>
                                            <input
                                                id="search-tournament"
                                                type="text"
                                                value={search}
                                                onChange={(e) => {
                                                    setPage(0);
                                                    setSearch(e.target.value);
                                                }}
                                                className="form-control pe-5 bg-lightgrey"
                                                placeholder="Buscar aqui."
                                                aria-label="Buscar torneios"
                                                style={{ paddingRight: 40 }}
                                            />
                                            <span
                                                className="position-absolute top-50 end-0 translate-middle-y me-3"
                                                style={{ pointerEvents: 'none' }}
                                            >
                                                <i className="bi bi-search" style={{ fontSize: 18, color: '#888' }}></i>
                                            </span>
                                        </div>
                                        <button
                                            id='filter-chain'
                                            ref={filterBtnRef}
                                            className="btn bg-lightgrey  d-flex align-items-center"
                                            style={{ height: 48 }} // mesma altura do input padrão Bootstrap
                                            onClick={() => setFilterOpen(true)}
                                        >
                                            <i className="bi bi-filter font-xxl" ></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {loadingFiltered && (
                                <div className="col-12">
                                    <Load />
                                </div>
                            )}
                            {error && (
                                <div className="col-12">
                                    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                                        <div className="card-body text-center">
                                            <p className="text-danger mb-0">Erro ao carregar torneios.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!loadingFiltered && !error && eventsList.length === 0 && (
                                <div className="col-12">
                                    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                                        <div className="card-body text-center">
                                            <p className="mb-0">Nenhum torneio encontrado.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {eventsList.map((value, index) => (
                                <div key={index} className="col-lg-6 col-md-12 pe-2 ps-2">
                                    <div className="card p-3 bg-white w-100 hover-card border-0 shadow-xss rounded-xxl mb-3 overflow-hidden" style={{ height: '400px' }}>
                                        <div className="card-image w-100" style={{ height: '300px' }}>
                                            <img src={`${value.imageUrl}`} alt="event" className="w-100 rounded-3" style={{ height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div className="card-body d-flex ps-0 pe-0 pb-0 ">
                                            <div className="bg-greylight me-3 p-3 border-light-md rounded-xxl theme-dark-bg">
                                                <h4 className="fw-700 font-lg ls-3 text-grey-900 mb-0">
                                                    <span className="ls-3 d-block font-xsss text-grey-500 fw-500">{value.month}</span>{value.date}
                                                </h4>
                                            </div>
                                            <h2 className="fw-700 lh-3 font-xss">
                                                {value.name}
                                                <span className="d-flex font-xssss fw-500 mt-2 lh-3 text-grey-500">
                                                    <i className="ti-location-pin me-1"></i>{value.location}
                                                </span>
                                            </h2>
                                            <div className="ms-auto p-0">
                                                <Link to={`/tournamentsingle/${value.id}`} className="font-xsss fw-700 ps-3 pe-3 lh-38 py-2 float-right mt-4 text-uppercase rounded-3 ls-2 btn-primary d-inline-block text-white me-1">ENTRAR</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredData?.torneiosFiltrados && (
                                <div className="col-12">
                                    <div className="d-flex justify-content-center my-3 gap-2">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                                            disabled={(filteredData?.torneiosFiltrados?.page ?? 0) <= 0}
                                        >
                                            Anterior
                                        </button>
                                        {Array.from({ length: filteredData.torneiosFiltrados.totalPages }, (_, i) => (
                                            <button
                                                key={i}
                                                className={`btn ${i === filteredData.torneiosFiltrados.page ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => setPage(i)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setPage((p) => p + 1)}
                                            disabled={
                                                (filteredData?.torneiosFiltrados?.page ?? 0) >=
                                                ((filteredData?.torneiosFiltrados?.totalPages ?? 1) - 1)
                                            }
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <TournamentFilterModal
                open={filterOpen}
                onClose={() => {
                    setFilterOpen(false);
                    filterBtnRef.current?.focus();
                }}
                onFilter={(f) => {
                    setPage(0);
                    setFilters(f);
                }}
            />
        </Fragment>
    );
};

export default Event;
